/**
 * @providesModule SKZToaster
 */

import React from 'react';
import NotificationNavigationController from 'NotificationNavigationController';
import NotificationToast from 'NotificationToast';

export type ToastVariant = 'success' | 'error' | 'warning';

const IconByVariantMap: Record<ToastVariant, string> = {
  success: 'merge_account_icon.svg',
  error: 'icon_x_gradient_background.svg',
  warning: 'icon_warning_yellow.svg',
};

type IToastDuration = 'short' | 'medium' | 'long';

export const TOAST_DURATIONS: Record<IToastDuration, number> = {
  short: 3000,
  medium: 5000,
  long: 10000,
};

/**
 * SKZToaster is a utility class that provides a static method for displaying toast notifications.
 * It uses the existing `NotificationNavigationController` and `NotificationToast` components
 * to show a toast with a specified variant and message.
 *
 * @class SKZToaster
 * @static
 * @type {ToastVariant} variant - The variant of the toast notification (e.g., 'success').
 * @type {string} message - The message to be displayed in the toast notification.
 *
 * @example
 * // Usage example:
 * SKZToaster.show('success', _m('Hello World'));
 */
export default class SKZToaster {
  /**
   * Displays a toast notification with the given message.
   * @param {string} variant - The variant of the toast notification.
   * @param {string} message - The message to be displayed in the toast notification.
   * @param {number} [duration=3000] - The duration in milliseconds for which the toast should be visible.
   */
  static show(variant: ToastVariant, message: string, duration = 3000): void {
    if (!variant || !message) {
      console.error('SKZToaster: Missing variant or message');
      return;
    }

    NotificationNavigationController.showInAppToast(
      <NotificationToast
        toastType={'mergeAccount'}
        primaryText={message}
        primaryTextFontSize={16}
        icon={IconByVariantMap[variant]}
        onDismiss={() => NotificationNavigationController.hideInAppToast()}
      />,
      {
        lifetime: duration,
        animated: true,
      }
    );
  }
}
